import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';

import { useLanguage } from '@/contexts/LanguageContext';

const VendorOnboardingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  

  const [formData, setFormData] = useState({
    business_name: '',
    business_address: '',
    phone_number: '',
    business_description: '',
    business_type: 'restaurant' as 'restaurant' | 'marketplace',
    categories: [] as string[],
  });

  const restaurantCategories = [
    'fast_food',
    'coffee', 
    'healthy',
    'dessert',
    'fine_dining',
    'casual_dining',
    'bakery',
    'grocery'
  ];

  const marketplaceCategories = [
    'groceries',
    'beauty',
    'technology',
    'clothing',
    'home_essentials',
    'services'
  ];

  const categories = formData.business_type === 'restaurant' 
    ? restaurantCategories 
    : marketplaceCategories;


  const handleCategoryChange = (category: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      categories: checked 
        ? [...prev.categories, category]
        : prev.categories.filter(c => c !== category)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: t('error'),
        description: "You must be logged in to submit an application",
        variant: "destructive",
      });
      return;
    }

    if (formData.categories.length === 0) {
      toast({
        title: t('error'), 
        description: "Please select at least one category",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase
        .from('vendor_applications')
        .insert({
          user_id: user.id,
          business_name: formData.business_name,
          business_address: formData.business_address,
          phone_number: formData.phone_number,
          business_description: formData.business_description,
          business_type: formData.business_type,
          categories: formData.categories,
        });

      if (error) throw error;

      toast({
        title: t('success'),
        description: "Your application has been submitted! We'll review it and get back to you soon.",
      });

      navigate('/profile');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: t('error'),
        description: "Failed to submit application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-xl font-semibold">{t('become_a_vendor')}</h1>
        </div>
      </div>

      <div className="px-4 py-6">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>{t('vendor_application')}</CardTitle>
            <CardDescription>
              Join GAMBGO as a restaurant or marketplace partner and start reaching more customers.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="business_name">{t('business_name')} *</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder={t('enter_business_name')}
                  required
                />
              </div>

              <div className="space-y-3">
                <Label>Business Type *</Label>
                <RadioGroup
                  value={formData.business_type}
                  onValueChange={(value: 'restaurant' | 'marketplace') => 
                    setFormData(prev => ({ ...prev, business_type: value, categories: [] }))
                  }
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="restaurant" id="restaurant" />
                    <Label htmlFor="restaurant" className="font-normal cursor-pointer">
                      Restaurant
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="marketplace" id="marketplace" />
                    <Label htmlFor="marketplace" className="font-normal cursor-pointer">
                      Marketplace
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="business_address">{t('business_address')} *</Label>
                <Input
                  id="business_address"
                  value={formData.business_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_address: e.target.value }))}
                  placeholder={t('enter_business_address')}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">{t('phone_number')} *</Label>
                <div className="flex">
                  <div className="flex items-center bg-gray-50 border border-r-0 border-gray-200 px-3 rounded-l-md">
                    <span className="text-sm text-gray-600">+220</span>
                  </div>
                  <Input
                    id="phone_number"
                    value={formData.phone_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="7012345"
                    className="rounded-l-none"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="business_description">{t('business_description')}</Label>
                <Textarea
                  id="business_description"
                  value={formData.business_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, business_description: e.target.value }))}
                  placeholder={t('tell_us_about_business')}
                  rows={4}
                />
              </div>

              <div className="space-y-3">
                <Label>{t('business_categories')} *</Label>
                <div className="grid grid-cols-2 gap-3">
                  {categories.map((category) => (
                    <div key={category} className="flex items-center space-x-2">
                      <Checkbox
                        id={category}
                        checked={formData.categories.includes(category)}
                        onCheckedChange={(checked) => handleCategoryChange(category, checked as boolean)}
                      />
                      <Label htmlFor={category} className="capitalize">
                        {t(category)}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading}>
                {loading ? t('submitting') : t('submit_application')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VendorOnboardingPage;
